import BaseView from '../views/BaseView';
import Vol3PurchaseCarousel from './Vol3PurchaseCarousel';
import SoundtrackPlayer from './SoundtrackPlayer';
import Vol3BGAnim from './Vol3BGAnim';
import CRelatedStoryCarousel from '../components/CRelatedStoryCarousel';
import debounce from 'lodash.debounce';
import { scrollPageTo } from '../utils';
import scrollLock from 'scroll-lock';
import PageSlider from '../modules/PageSlider';

export default class Chapter extends BaseView {
	constructor(container) {
		super(container);

		this.breakpoint = window.matchMedia('(max-width:767.5px)');
	}

	init() {
		const groups = new Map([
			[CRelatedStoryCarousel, this.container.querySelectorAll('[data-c-related-story-carousel]')],
			[PageSlider, this.container.querySelectorAll('[data-page-slider]')],
		]);

		super.createModules(groups);

		this.header = this.container.querySelector('[data-header]');

		this.nav = this.container.querySelector('[data-nav]');
		this.navLinks = this.container.querySelectorAll('[data-nav-link]');

		this.navButton = this.container.querySelector('[data-nav-button]');

		this.bgContainer = this.container.querySelector('[data-bg-container]');
		this.bgWrapper = this.container.querySelector('[data-bg-wrapper]');
		this.images = this.container.querySelectorAll('.v3-ch-bg-container__image');
		this.storyHovers = this.container.querySelectorAll('[data-image-target]');

		this.indicator = this.container.querySelector('[data-scrol-indicator]');
		this.indicator.addEventListener('click', () => {
			scrollPageTo(window.innerHeight, 500);
		});

		this.stories = this.container.querySelectorAll('[data-story]');
		this.activeImage = null;

		this.soundtrackPlayer = new SoundtrackPlayer(this.container);

		this.percentage = 0;
		this.bgOffset = 0;

		this.bg = new Vol3BGAnim(this);

		// Bind Resize Event
		this.boundResizeListener = debounce(this.resizeListener.bind(this), 400);
		window.addEventListener('orientationchange', this.boundResizeListener);
		window.addEventListener('resize', this.boundResizeListener);
		this.resizeListener();

		// Bind Scroll Event
		this.boundScrollListener = this.scrollListener.bind(this);
		document.addEventListener('scroll', this.boundScrollListener);

		// Nav Title Hovers
		this.navLinks.forEach((link) => {
			link.addEventListener('mouseenter', () => {
				const target = link.getAttribute('data-nav-link');
				const targetTitle = this.container.querySelector('[data-nav-hover="' + target + '"]');
				if (targetTitle) {
					targetTitle.classList.add('v3-ch-nav-hover-story--visible');
				}
			});
			link.addEventListener('mouseleave', () => {
				const target = link.getAttribute('data-nav-link');
				const targetTitle = this.container.querySelector('[data-nav-hover="' + target + '"]');
				if (targetTitle) {
					targetTitle.classList.remove('v3-ch-nav-hover-story--visible');
				}
			});
		});

		// Story Background Hovers
		this.storyHovers.forEach((story) => {
			story.addEventListener('mouseenter', () => {
				const target = story.getAttribute('data-image-target');
				if (target) {
					this.activeImage = target;

					this.resizeListener();
				}
			});
		});

		this.active = true;

		// Nav Button Mobile
		this.menuOpen = false;
		this.navButton.addEventListener('click', () => {
			if (this.menuOpen) {
				this.nav.classList.remove('v3-ch-nav--open');
				this.navButton.classList.remove('v3-ch-chapter-button--active');
				this.bgContainer.classList.remove('v3-ch-container--transparent');
				this.menuOpen = false;
				scrollLock.enablePageScroll();
			} else {
				this.nav.classList.add('v3-ch-nav--open');
				this.navButton.classList.add('v3-ch-chapter-button--active');
				this.bgContainer.classList.add('v3-ch-container--transparent');

				document.body.classList.remove('has-scrolled-up');
				document.body.classList.add('has-scrolled-down');

				this.menuOpen = true;
				scrollLock.disablePageScroll();

				const navButtonRect = this.navButton.getBoundingClientRect();
				const navButtonBottom = window.innerHeight - navButtonRect.bottom;
				if (navButtonBottom != 18) {
					scrollPageTo(this.navButton, 500, -1 * (window.innerHeight - 20));
				}
			}
		});

		// Purchase Carousel (Mobile only)
		this.purchaseCarousel = this.container.querySelector('[data-purchase-carousel]');
		if (this.purchaseCarousel) {
			new Vol3PurchaseCarousel(this.purchaseCarousel);
		}

		// Info Panel
		this.infoButton = this.container.querySelector('[data-info-button]');
		this.closeInfoButton = this.container.querySelector('[data-close-info-button]');
		this.infoPanel = this.container.querySelector('[data-info-panel]');
		this.infoPanelOverlay = this.container.querySelector('[data-info-panel-overlay]');
		this.infoPanelPlayerEl = this.container.querySelector('[data-info-panel-player]');
		this.infoPanelNav = this.container.querySelector('[data-info-panel-nav]');

		this.infoOpen = false;
		this.infoPanelOverlay.addEventListener('click', () => {
			this.toggleInfo();
		});
		this.infoButton.addEventListener('click', () => {
			this.toggleInfo();
		});
		this.closeInfoButton.addEventListener('click', () => {
			this.toggleInfo();
		});
		this.infoPanel.addEventListener('click', (e) => {
			if (e.target === this.infoPanel) {
				this.toggleInfo();
			}
		});
		this.infoPanelNav.addEventListener('click', (e) => {
			const a = e.target.closest('a');

			if (!a) {
				return;
			}

			e.preventDefault();
			scrollPageTo(a.hash, 1000, 0, this.infoPanel);
		});

		function buildThresholdList() {
			let thresholds = [];
			let numSteps = 20;

			for (let i = 1.0; i <= numSteps; i++) {
				let ratio = i / numSteps;
				thresholds.push(ratio);
			}

			thresholds.push(0);
			return thresholds;
		}

		// Update info panel nav active state
		if ('IntersectionObserver' in window) {
			this.infoPanelObserver = new IntersectionObserver(
				(entries, observer) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
							const activeAnchor = this.infoPanelNav.querySelector('.is-active');

							if (activeAnchor) {
								activeAnchor.classList.remove('is-active');
							}

							const id = entry.target.getAttribute('id');
							const newActiveAnchor = this.infoPanelNav.querySelector(`[href="#${id}"]`);

							if (newActiveAnchor) {
								newActiveAnchor.classList.add('is-active');
							}
						}
					});
				},
				{
					root: this.infoPanel,
					threshold: buildThresholdList(),
				},
			);
		}

		this.container.querySelectorAll('.v3-ch-info-panel-section').forEach((section) => {
			this.infoPanelObserver.observe(section);
		});

		this.boundChangeInfoPlayerImage = this.changeInfoPlayerImage.bind(this);
		document.addEventListener('inlineaudioplaying', this.boundChangeInfoPlayerImage);
	}

	show() {
		this.header.classList.remove('v3-ch-header--hidden');
	}

	changeInfoPlayerImage(e) {
		const id = e.detail.dataset.id;

		this.infoPanelPlayerEl.querySelectorAll('[data-artwork-id]').forEach((el) => {
			if (el.dataset.artworkId == id) {
				el.classList.add('is-active');
			} else {
				el.classList.remove('is-active');
			}
		});
	}

	toggleInfo() {
		if (this.infoOpen) {
			this.closeInfo();
		} else {
			this.openInfo();
		}
	}

	openInfo() {
		this.infoPanel.scrollTo(0, 0);

		this.infoPanel.classList.add('v3-ch-info-panel--open');
		this.infoPanelOverlay.classList.add('v3-ch-info-panel-overlay--open');
		this.infoOpen = true;
		scrollLock.disablePageScroll(this.infoPanel);

		this.bgContainer.classList.add('v3-ch-container--transparent');
		document.body.classList.remove('has-scrolled-up');
		document.body.classList.add('has-scrolled-down');
	}

	closeInfo() {
		this.infoPanel.classList.remove('v3-ch-info-panel--open');
		this.infoPanelOverlay.classList.remove('v3-ch-info-panel-overlay--open');
		this.infoOpen = false;
		scrollLock.enablePageScroll(this.infoPanel);

		if (!this.menuOpen) {
			this.bgContainer.classList.remove('v3-ch-container--transparent');
		}
		document.dispatchEvent(new CustomEvent('audioplayerplaying')); // Used to pause the inline audio players
	}

	scrollListener() {
		const percentage = (window.scrollY / this.bgContainerHeight) * 100;
		this.percentage = percentage;

		const navRect = this.nav.getBoundingClientRect();
		if (navRect.top === 0) {
			this.nav.classList.add('v3-ch-nav--sticky');
		} else {
			this.nav.classList.remove('v3-ch-nav--sticky');
		}

		if (window.scrollY > 10) {
			this.indicator.classList.add('v3-ch-scroll-indicator--hidden');
		} else {
			return;
		}

		if (!this.breakpoint.matches && !window.matchMedia('(pointer: coarse)').matches) {
			// Desktop
			if (navRect.top >= window.innerHeight * 0.75) {
				if (this.activeImage) {
					this.activeImage = null;
				}
			}
		} else {
			// Mobile

			this.stories.forEach((story) => {
				const rect = story.getBoundingClientRect();
				const topSpace = rect.top;
				const bottomSpace = window.innerHeight - (rect.top + rect.height);

				let someVal = 0;
				if (bottomSpace < window.innerHeight) {
					someVal = window.innerHeight - topSpace;
				} else {
					someVal = window.innerHeight - bottomSpace;
				}

				let heightInScreen = 0;
				if (someVal > 0) {
					if (someVal < rect.height) {
						heightInScreen = window.innerHeight - topSpace;
					} else {
						heightInScreen = window.innerHeight - bottomSpace;
					}
				}

				if (heightInScreen >= rect.height) {
					heightInScreen = rect.height;
				}

				const opacity = ((heightInScreen / rect.height) * 1).toFixed(2);
				if (opacity > 0) {
					story.setAttribute('data-mobile-opacity', opacity);
				} else {
					story.setAttribute('data-mobile-opacity', 0);
				}
			});
		}
	}

	resizeListener() {
		this.setOuterHeight();

		this.bgContainerHeight = this.bgContainer.scrollHeight;
		this.bg.resize();

		this.scrollListener();
	}

	setOuterHeight() {
		let outerHeight = window.outerHeight;
		let innerHeight = window.innerHeight;
		if (innerHeight > outerHeight) {
			document.documentElement.style.setProperty('--outerHeight', `${innerHeight}px`);
		} else {
			document.documentElement.style.setProperty('--outerHeight', `${outerHeight}px`);
		}
	}

	destroy() {
		this.active = false;
		document.removeEventListener('scroll', this.boundScrollListener);
		window.removeEventListener('resize', this.boundResizeListener);
		window.removeEventListener('orientationchange', this.boundResizeListener);
		document.removeEventListener('inlineaudioplaying', this.boundChangeInfoPlayerImage);

		if (this.infoPanelObserver) {
			this.infoPanelObserver.disconnect();
		}

		scrollLock.enablePageScroll();
	}
}
